import { FunctionComponent, useMemo, useState } from "react";

import { Link, useNavigate } from "react-router-dom";

import { Trans, useTranslation } from "react-i18next";

import { Bell, Coins, LogOut, User } from "lucide-react";

import { classNames } from "@/utils/classNames";

import { ROUTE_CONFIG } from "@/routes/config";

import { PermissionRoles } from "@/enum";
import { useHasPermissions } from "@/utils/useHasPermissions";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import { useGetCreditsQuery } from "@/pages/Private/redux/usage/usage.api";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { useIsFeatureActiveUnauthenticated } from "@/utils/useIsFeatureActiveUnauthenticated";
import { useGetUnreadCountQuery } from "@/pages/Private/redux/notification/notification.api";
import {
	notificationSelector,
	togglePanel,
} from "@/pages/Private/redux/notification/notification.slice";

import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";
import { getCancellationInfo } from "@/pages/Private/pages/Usage/pages/utils/cancellation.utils";

import { ContextMenu } from "../ContextMenu/ContextMenu";
import { Tooltip } from "../Tooltip/Tooltip";

import { SidebarSetupGuide } from "./SidebarSetupGuide";
import { resolveEmptyCreditsLabelKey } from "./emptyCreditsLabel";

interface SidebarFooterProps {
	isExpanded: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	user: any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	initialUser: any;
	onResetInitialUser: () => void;
}

const ICON_CLASS = "w-[19px] h-[19px] flex-shrink-0";

export const SidebarFooter: FunctionComponent<SidebarFooterProps> = ({
	isExpanded,
	user,
	initialUser,
	onResetInitialUser,
}) => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { t } = useTranslation();
	const ts = (key: string) => t(`sidebar.${key}`);

	const { hasPermissions } = useHasPermissions();
	const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);

	const { data: credits, isFetching: isCreditsLoading } = useGetCreditsQuery(undefined, {
		refetchOnMountOrArgChange: true,
	});

	const selfRegister = useIsFeatureActiveUnauthenticated(FeaturesCodes.SELF_REGISTER);
	const isDemo = useMemo(
		() => credits?.perMonthOrYear?.toLowerCase() === "demo" && selfRegister === true,
		[credits, selfRegister]
	);
	// The same pending-cancellation test that gives the billing page its Reactivate button.
	const isCancellationPending = useMemo(
		() => getCancellationInfo(credits?.stripeSubscription, credits?.cancellation) !== null,
		[credits]
	);

	const isNotificationCenterActive = useIsFeatureActive(FeaturesCodes.NOTIFICATION_CENTER);
	const { unreadCount } = useAppSelector(notificationSelector);

	useGetUnreadCountQuery(undefined, {
		skip: !isNotificationCenterActive,
		refetchOnMountOrArgChange: true,
	});

	const rowClass = classNames(
		"flex items-center h-9 rounded-[9px] cursor-pointer transition-colors text-text-meta hover:bg-sidebar-item-hover",
		isExpanded ? "gap-[11px] px-[10px] w-full" : "justify-center w-11 mx-auto"
	);

	const renderCredits = () => {
		if (!hasPermissions([PermissionRoles.SHOW_CREDITS])) {
			return null;
		}

		if (isCreditsLoading) {
			return (
				<div className={rowClass}>
					<Tooltip
						content={ts("loading")}
						direction="top-right"
						disabled={isExpanded}
						variant="dark"
					>
						<Coins className={classNames(ICON_CLASS, "text-placeholder")} strokeWidth={1.9} />
					</Tooltip>
					{isExpanded && <span className="text-sm whitespace-nowrap">{ts("loading")}</span>}
				</div>
			);
		}

		const hasNoCredits = credits && credits.credits <= 0;
		const emptyLabel = ts(resolveEmptyCreditsLabelKey({ isCancellationPending, isDemo }));
		const creditsLabel = (
			<Trans
				t={t}
				i18nKey="sidebar.creditsLeft"
				values={{ count: credits?.credits || 0 }}
				components={{ bold: <b /> }}
			/>
		);

		return (
			<Link className={rowClass} to="/app/company?tab=BILLING">
				<Tooltip
					content={hasNoCredits ? emptyLabel : creditsLabel}
					direction="top-right"
					disabled={isExpanded}
					variant="dark"
				>
					<Coins
						className={classNames(
							ICON_CLASS,
							hasNoCredits ? "text-status-error" : "text-placeholder"
						)}
						strokeWidth={1.9}
					/>
				</Tooltip>
				{isExpanded &&
					(hasNoCredits ? (
						<span className="text-sm whitespace-nowrap">{emptyLabel}</span>
					) : (
						<span className="text-sm whitespace-nowrap">
							<Trans
								t={t}
								i18nKey="sidebar.creditsLeft"
								values={{ count: credits?.credits || 0 }}
								components={{ bold: <b className="font-bold text-text" /> }}
							/>
						</span>
					))}
			</Link>
		);
	};

	return (
		<div
			className={classNames(
				"sticky bottom-0 z-[151] flex flex-col gap-0.5 border-t border-t-gray-200 pt-2 pb-3",
				initialUser ? "bg-other-org" : "bg-brand-white",
				// Collapsed: centre every row (coin / bell / avatar) on the same axis. The
				// avatar sits inside Tooltip→ContextMenu wrappers, so its own mx-auto can't
				// centre it against the sibling rows — the flex column's items-center does.
				isExpanded ? "px-3" : "px-0 items-center"
			)}
		>
			<SidebarSetupGuide isExpanded={isExpanded} rowClass={rowClass} />

			{renderCredits()}

			{isNotificationCenterActive && (
				<div className={rowClass} onClick={() => dispatch(togglePanel())}>
					<Tooltip
						content={ts("notifications")}
						direction="top-right"
						disabled={isExpanded}
						variant="dark"
					>
						<span className="relative flex">
							<Bell className={classNames(ICON_CLASS, "text-placeholder")} strokeWidth={1.9} />
							{unreadCount > 0 && (
								<span className="absolute -top-1.5 -right-2 min-w-3.5 h-3.5 px-1 flex items-center justify-center text-xxs font-bold text-white bg-status-error rounded-full">
									{unreadCount > 99 ? "99+" : unreadCount}
								</span>
							)}
						</span>
					</Tooltip>
					{isExpanded && (
						<span className="text-sm whitespace-nowrap">
							{ts("notifications")}
							{unreadCount > 0 && (
								<span className="ml-1 text-xs text-text-meta">
									({unreadCount > 99 ? "99+" : unreadCount})
								</span>
							)}
						</span>
					)}
				</div>
			)}

			<Tooltip
				content={
					<>
						<h5 className="font-bold mb-2">{ts("personalSettings")}</h5>
						<p>{ts("personalSettingsTooltip")}</p>
					</>
				}
				disabled={isAvatarMenuOpen}
			>
				<ContextMenu
					data={[
						[
							{
								title: ts("myProfile"),
								icon: <User className="w-[20px] h-[20px] text-gray-700" />,
								onClick: () => navigate(ROUTE_CONFIG.ACCOUNT),
							},
							{
								title: initialUser ? ts("back") : ts("logout"),
								icon: <LogOut className="w-[20px] h-[20px] text-gray-700" />,
								onClick: () => (initialUser ? onResetInitialUser() : navigate("/logout")),
							},
						],
					]}
					position="top-right"
					width={isExpanded ? "auto" : "150"}
					onOpenChange={setIsAvatarMenuOpen}
				>
					<div
						className={classNames(rowClass, "mt-0.5")}
						data-onboarding="connection-sync-user-icon"
					>
						<span className="min-w-[24px] h-[24px] flex items-center justify-center text-xxs font-bold text-white rounded-full bg-blue-action">
							{user?.firstName?.[0]}
							{user?.lastName?.[0]}
						</span>
						{isExpanded && (
							<span className="text-sm font-semibold text-text whitespace-nowrap truncate">
								{user?.firstName} {user?.lastName}
							</span>
						)}
					</div>
				</ContextMenu>
			</Tooltip>
		</div>
	);
};
